import { createFileRoute } from "@tanstack/react-router";
import { useLocale, useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useSetLocale } from "@/i18n/locale-provider";
import type { Locale } from "@/i18n/routing";
import { NotificationPreferencesCard } from "@/components/notification-preferences-card";

export const Route = createFileRoute("/_dashboard/settings")({ component: SettingsPage });

function SettingsPage() {
  const t = useTranslations("SettingsPage");
  const locale = useLocale();
  const { setLocale } = useSetLocale();
  const { theme, setTheme } = useTheme();

  const themeOptions = ["light", "dark", "system"] as const;
  const localeOptions: Array<{ value: Locale; label: string }> = [
    { value: "it", label: t("italian") },
    { value: "en", label: t("english") },
  ];

  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <div>
          <h1 className="text-2xl font-bold">{t("title")}</h1>
          <p className="text-muted-foreground text-sm">{t("description")}</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>{t("appearance")}</CardTitle>
            <CardDescription>{t("appearanceDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {themeOptions.map((option) => (
              <Button
                key={option}
                type="button"
                variant={theme === option ? "default" : "outline"}
                aria-pressed={theme === option}
                onClick={() => setTheme(option)}
              >
                {t(option)}
              </Button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("language")}</CardTitle>
            <CardDescription>{t("languageDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {localeOptions.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant={locale === option.value ? "default" : "outline"}
                aria-pressed={locale === option.value}
                onClick={() => setLocale(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </CardContent>
        </Card>

        <NotificationPreferencesCard />

        <p className="text-muted-foreground text-xs">
          {t("savedAutomatically")}
        </p>
      </div>
    </>
  );
}
